import { useEffect, useState } from 'react';
import { UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { EditableQuestion } from '../../../../../types';
import { useAnswerSelectionField } from '../components/AnswerSelectionEditor/hooks/useAnswerSelectionField';

/**
 * Presence of the value is the flag, so the switch is derived from the question rather
 * than persisted as a separate boolean. Mirrors useStructuredInput.
 *
 * Hiding only means anything on a single radio selection. React Hook Form keeps the
 * values of unmounted inputs, so a question retyped to something else would otherwise
 * still save a hidden selection the editor no longer shows — and the api refuses to hide
 * a selection the question does not have.
 */
export const useHiddenAnswerSelection = (
  question: EditableQuestion,
  watch: UseFormWatch<EditableQuestion>,
  setValue: UseFormSetValue<EditableQuestion>,
  isOffered: boolean
) => {
  const [isSwitchedOn, setIsSwitchedOn] = useState(question.answer_selection === 'hidden');

  // Derived as well as reset below, because the reset lands after the render in which the
  // question stopped being a single radio selection.
  const isAnswerSelectionHidden = isOffered && isSwitchedOn;

  useAnswerSelectionField(isAnswerSelectionHidden, watch, setValue);

  // Retyping the question says the selection is displayed again, so the switch has to
  // come back off. Left on, it would offer to hide a selection that is no longer there.
  useEffect(() => {
    if (!isOffered) {
      setIsSwitchedOn(false);
    }
  }, [isOffered]);

  return { isAnswerSelectionHidden, setIsAnswerSelectionHidden: setIsSwitchedOn };
};
